/**
 * CatalogFilterBar — the shop's sort selector (DESIGN frame 04).
 *
 * Controlled by {@link useCatalogFilter}; emits intent only — the server does the
 * filtering/sorting. Rendered as a pill button bearing the current sort label and a
 * sort icon, with a native ``<select>`` overlaid for accessible option choice. Sort
 * options come from the product entity's canonical list.
 */
import {
  PRODUCT_SORT_OPTIONS,
  type ProductSort,
} from "@entities/product";

import type { CatalogFilterState } from "../model";
import styles from "./CatalogFilterBar.module.css";

export interface CatalogFilterBarProps {
  readonly filter: CatalogFilterState;
  /** Optional leading count line (e.g. "16 услуг"). */
  readonly countLabel?: string;
}

const currentLabel = (sort: ProductSort): string =>
  PRODUCT_SORT_OPTIONS.find((option) => option.value === sort)?.label ??
  "Сортировка";

export function CatalogFilterBar({
  filter,
  countLabel,
}: CatalogFilterBarProps): JSX.Element {
  return (
    <div className={styles.bar}>
      {countLabel ? (
        <span className={styles.count}>{countLabel}</span>
      ) : (
        <span aria-hidden="true" />
      )}
      <div className={styles.sortWrap}>
        <span className={styles.sortFace} aria-hidden="true">
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M4 6h9" />
            <circle cx="16" cy="6" r="2.4" />
            <path d="M19 6h1" />
            <path d="M4 12h3" />
            <circle cx="10" cy="12" r="2.4" />
            <path d="M13 12h7" />
            <path d="M4 18h9" />
            <circle cx="16" cy="18" r="2.4" />
            <path d="M19 18h1" />
          </svg>
          <span className={styles.sortLabel}>{currentLabel(filter.sort)}</span>
        </span>
        <select
          className={styles.sort}
          aria-label="Сортировка"
          value={filter.sort}
          onChange={(event) => {
            filter.setSort(event.target.value as ProductSort);
          }}
        >
          {PRODUCT_SORT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
